import { config } from "@/config";
import Table from "@commons/Table/TableContent";
import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useGetSamples } from "@services/samples/list";
import { EventSampleColumns } from "@typings/entities/individuals/additionalData/table";
import type { SampleItemList } from "@services/samples/list/type";
import { formatDateTime } from "@utils/formatters";
import { getTypeLabel } from "@typings/entities/samples/enums";
import type { SampleItemListWithLabels } from "@utils/translator/samples";
import { useNavigate } from "react-router-dom";

function prepareSample(items: SampleItemList[]): SampleItemListWithLabels[] {
  return items.map((item) => ({
    ...item,
    reporterName: item.reporterName || "-",
    type: getTypeLabel(item.type) || "-",
    sampleDate: formatDateTime(item.sampleDate) || "",
    attachmentsCount: item.attachmentsCount || 0,
  }));
}

function SampleTable({ individualId }: { individualId: string }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);
  const navigate = useNavigate();

  const {
    data: samples,
    isLoading,
    error,
  } = useGetSamples({
    page: page + 1,
    limit: rowsPerPage,
    individualId,
  });

  useEffect(() => {
    if (error) {
      toast.error("Erro ao carregar amostras. Tente novamente.");
    }
  }, [error]);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  const handleView = (sample: SampleItemListWithLabels) => {
    navigate(`/samples/${sample.id}`);
  };

  const heads = EventSampleColumns;

  const processedData = prepareSample(samples?.items ?? []);

  if (error) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center py-8">
        <ErrorOutlineIcon className="text-red-500 mb-2 text-6xl" />
        <span className="text-red-600 text-center">
          Ocorreu um erro ao carregar as amostras.
        </span>
      </div>
    );
  }

  return (
    <Table.Root isLoading={isLoading}>
      <Table.Head heads={heads} actions={true} />
      <Table.Body
        rows={processedData ?? []}
        columns={heads.map((head) => head.id)}
        actions={["view"]}
        onView={handleView}
      />
      <Table.Footer
        colSpan={heads.length}
        count={samples?.meta.totalItems ?? 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Table.Root>
  );
}

export default SampleTable;
